import React, { useEffect, useState, useRef } from 'react';
import { COLORS } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import {
  listPhiRecords, uploadPhiRecord, issuePhiSignedUrl, deletePhiRecord,
  fetchPhiConsent, fetchPhiTemplates, downloadPhiTemplate,
} from '../services/api';
import {
  Upload, FileText, Image, Activity, Trash2, Download, Loader2,
  ShieldCheck, Lock, AlertTriangle, Sparkles, FileDown, ClipboardList,
} from 'lucide-react';
import { humanizeApiError } from '../lib/errors';
import ResumableUploadModal from '../components/ResumableUploadModal';
import HipaaConsentModal from '../components/HipaaConsentModal';

const RECORD_TYPES = [
  { id: 'lab', label: 'Lab Result' },
  { id: 'imaging', label: 'Imaging' },
  { id: 'note', label: 'Clinical Note' },
  { id: 'dicom', label: 'DICOM' },
  { id: 'fhir', label: 'FHIR / HL7' },
  { id: 'csv', label: 'CSV / Values' },
  { id: 'other', label: 'Other' },
];

function fmtSize(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function iconFor(type) {
  if (type === 'imaging' || type === 'dicom') return Image;
  if (type === 'lab' || type === 'csv') return Activity;
  return FileText;
}

export default function MedicalRecordsPage() {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [recordType, setRecordType] = useState('lab');
  const [recordDate, setRecordDate] = useState('');
  const [notes, setNotes] = useState('');
  const [resumableOpen, setResumableOpen] = useState(false);
  // HIPAA consent gating — null while loading, then boolean.
  const [consented, setConsented] = useState(null);
  const [showConsent, setShowConsent] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);
  const fileRef = useRef(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await listPhiRecords({ limit: 100 });
      setRecords(r.records || []);
    } catch (e) {
      setError(humanizeApiError(e, 'Failed to load records'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    fetchPhiTemplates().then(r => setTemplates(r.templates || [])).catch(() => {});
    fetchPhiConsent()
      .then(c => {
        setConsented(!!c.agreed);
        if (!c.agreed) setShowConsent(true);
      })
      .catch(() => setConsented(false));
  }, []);

  const requireConsent = () => {
    if (consented) return true;
    setShowConsent(true);
    return false;
  };

  const onDownloadTemplate = async (tpl) => {
    if (!requireConsent()) return;
    setError('');
    setDownloadingId(tpl.template_id);
    try {
      await downloadPhiTemplate(tpl.template_id, tpl.filename);
    } catch (e) {
      setError(humanizeApiError(e, 'Template download failed'));
    } finally {
      setDownloadingId(null);
    }
  };

  const onUpload = async (e) => {
    e.preventDefault();
    if (!requireConsent()) return;
    const file = fileRef.current?.files?.[0];
    if (!file) { setError('Pick a file first'); return; }
    setError('');
    setUploading(true);
    try {
      await uploadPhiRecord(file, { record_type: recordType, record_date: recordDate, notes });
      fileRef.current.value = '';
      setNotes('');
      await reload();
    } catch (err) {
      setError(humanizeApiError(err, 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const onDownload = async (record) => {
    try {
      const { url_path } = await issuePhiSignedUrl(record.record_id, 120);
      const base = process.env.REACT_APP_BACKEND_URL || '';
      window.open(base + url_path, '_blank');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not generate download link');
    }
  };

  const onDelete = async (record) => {
    if (!window.confirm(`Permanently delete "${record.filename}"? This is irreversible.`)) return;
    try {
      await deletePhiRecord(record.record_id);
      await reload();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="medical-records-page" style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <ShieldCheck size={26} color={COLORS.primary} />
        <h1 style={{ fontSize: 26, fontWeight: 800, color: COLORS.textPrimary, margin: 0, fontFamily: 'Outfit, sans-serif' }}>
          My Medical Records
        </h1>
      </div>
      <p style={{ color: COLORS.textSecondary, marginTop: 0, fontSize: 14, lineHeight: 1.6, maxWidth: 720 }}>
        <Lock size={12} style={{ display: 'inline', marginRight: 6 }} />
        All records are encrypted at rest with AES-256-GCM. Downloads use single-use links that
        expire in 2 minutes. Every view is recorded in an immutable audit log.
      </p>

      {/* Consent status */}
      {consented === false && (
        <div data-testid="phi-consent-banner" style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          backgroundColor: '#FFF8E1', border: '1px solid #F0E0A0', borderRadius: 12,
          padding: '12px 16px', marginTop: 12,
        }}>
          <AlertTriangle size={18} color="#A65500" />
          <span style={{ flex: 1, fontSize: 13, color: COLORS.textPrimary, minWidth: 220 }}>
            Before downloading templates or uploading medical information you must review and
            accept our HIPAA disclaimer &amp; privacy policy.
          </span>
          <button data-testid="phi-consent-open-btn" onClick={() => setShowConsent(true)}
            style={{ backgroundColor: COLORS.primary, color: '#FFF', border: 'none', borderRadius: 9999,
                     padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            Review &amp; Agree
          </button>
        </div>
      )}
      {consented && (
        <div data-testid="phi-consent-ok" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 12, color: COLORS.success, fontWeight: 600 }}>
          <ShieldCheck size={14} /> HIPAA disclaimer &amp; privacy policy accepted
        </div>
      )}

      {/* Step 1 — Download templates */}
      <div data-testid="phi-templates-section" style={{
        backgroundColor: COLORS.surface, borderRadius: 16, padding: 24, marginTop: 20,
        border: `1px solid ${COLORS.surfaceSecondary}`,
      }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, marginTop: 0, marginBottom: 6, fontFamily: 'Outfit, sans-serif' }}>
          <ClipboardList size={18} color={COLORS.primary} /> Step 1 — Download medical information templates
        </h3>
        <p style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 0, marginBottom: 16, lineHeight: 1.5 }}>
          Fill these in with your lifetime medications, diagnoses, and procedures — including dates
          and outcomes — then encrypt &amp; upload them below.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
          {templates.map(tpl => (
            <div key={tpl.template_id} data-testid={`phi-template-${tpl.template_id}`}
              style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 14, borderRadius: 12,
                       border: `1px solid ${COLORS.surfaceSecondary}`, backgroundColor: COLORS.background }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileText size={16} color={COLORS.primary} />
                <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary }}>{tpl.name}</span>
              </div>
              <p style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.45, margin: 0, flex: 1 }}>{tpl.description}</p>
              <button onClick={() => onDownloadTemplate(tpl)} disabled={downloadingId === tpl.template_id}
                data-testid={`phi-template-download-${tpl.template_id}`}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                         backgroundColor: 'transparent', color: COLORS.primary, border: `1px solid ${COLORS.primary}`,
                         borderRadius: 9999, padding: '8px 14px', fontSize: 12, fontWeight: 700,
                         cursor: downloadingId === tpl.template_id ? 'wait' : 'pointer' }}>
                {downloadingId === tpl.template_id ? <Loader2 size={14} className="spin" /> : <FileDown size={14} />}
                Download template
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Step 2 — Upload card */}
      <form data-testid="phi-upload-form" onSubmit={onUpload} style={{
        backgroundColor: COLORS.surface, borderRadius: 16, padding: 24, marginTop: 20,
        border: `1px solid ${COLORS.surfaceSecondary}`,
        opacity: consented === false ? 0.65 : 1,
      }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, marginTop: 0, marginBottom: 16, fontFamily: 'Outfit, sans-serif' }}>
          <Upload size={18} color={COLORS.primary} /> Step 2 — Encrypt &amp; upload your records
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <input ref={fileRef} type="file" data-testid="phi-file-input"
            accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,.dcm,.dicom,.json,.txt,.csv,application/pdf,image/*,application/dicom,application/json,text/plain,text/csv"
            style={{ padding: 8, border: `1px solid ${COLORS.surfaceSecondary}`, borderRadius: 8 }} />
          <select data-testid="phi-record-type" value={recordType} onChange={e => setRecordType(e.target.value)}
            style={{ padding: 8, border: `1px solid ${COLORS.surfaceSecondary}`, borderRadius: 8 }}>
            {RECORD_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <input type="date" data-testid="phi-record-date" value={recordDate} onChange={e => setRecordDate(e.target.value)}
            style={{ padding: 8, border: `1px solid ${COLORS.surfaceSecondary}`, borderRadius: 8 }} />
          <input type="text" data-testid="phi-record-notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)"
            style={{ padding: 8, border: `1px solid ${COLORS.surfaceSecondary}`, borderRadius: 8 }} />
        </div>
        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
          <button type="submit" disabled={uploading} data-testid="phi-upload-submit"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                     backgroundColor: COLORS.primary, color: '#FFF', border: 'none',
                     borderRadius: 9999, padding: '10px 20px', fontWeight: 700, fontSize: 14,
                     cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.7 : 1 }}>
            {uploading ? <Loader2 size={16} className="spin" /> : <Upload size={16} />}
            {uploading ? 'Encrypting & uploading…' : 'Upload securely'}
          </button>
          <button type="button" data-testid="phi-resumable-open" onClick={() => { if (requireConsent()) setResumableOpen(true); }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                     backgroundColor: 'transparent', color: COLORS.primary, border: `1px solid ${COLORS.primary}`,
                     borderRadius: 9999, padding: '10px 20px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
            <Upload size={16} /> Large / Resumable
          </button>
          {error && (
            <span data-testid="phi-error" style={{ color: '#B22222', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <AlertTriangle size={14} /> {error}
            </span>
          )}
        </div>
      </form>

      {/* Records list */}
      <div style={{ marginTop: 24 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 12, fontFamily: 'Outfit, sans-serif' }}>
          {records.length === 0 ? 'No records yet' : `${records.length} record${records.length === 1 ? '' : 's'}`}
        </h3>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: COLORS.textSecondary }}>
            <Loader2 size={20} className="spin" />
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {records.map(r => {
              const Icon = iconFor(r.record_type);
              return (
                <div key={r.record_id} data-testid={`phi-record-${r.record_id}`}
                  style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 18px',
                           backgroundColor: COLORS.surface, borderRadius: 12,
                           border: `1px solid ${COLORS.surfaceSecondary}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: `${COLORS.primary}15`,
                                   display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon size={20} color={COLORS.primary} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: COLORS.textPrimary, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.filename}
                      </div>
                      <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                        <span>{r.record_type}</span>
                        <span>{fmtSize(r.size_bytes)}</span>
                        {r.record_date && <span>{r.record_date}</span>}
                        {user?.role === 'admin' && r.owner_email !== user.email && <span>👤 {r.owner_email}</span>}
                      </div>
                    </div>
                    <button onClick={() => onDownload(r)} data-testid={`phi-download-${r.record_id}`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                               backgroundColor: 'transparent', color: COLORS.primary,
                               border: `1px solid ${COLORS.primary}`, borderRadius: 8, padding: '6px 12px',
                               fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      <Download size={14} /> Download
                    </button>
                    <button onClick={() => onDelete(r)} data-testid={`phi-delete-${r.record_id}`}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: '#B22222' }}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                  {r.ai_summary?.bullets?.length > 0 && (
                    <div data-testid={`phi-ai-summary-${r.record_id}`}
                      style={{ borderLeft: `3px solid ${COLORS.primary}`, paddingLeft: 12, marginLeft: 54, fontSize: 13, color: COLORS.textPrimary }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: COLORS.primary, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                        <Sparkles size={12} /> AI Insight
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.55 }}>
                        {r.ai_summary.bullets.map((b, i) => (
                          <li key={i} style={{ marginBottom: 3 }}>{b}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ResumableUploadModal
        open={resumableOpen}
        onClose={() => setResumableOpen(false)}
        onUploaded={() => { setResumableOpen(false); reload(); }}
        defaultRecordType={recordType}
      />

      <HipaaConsentModal
        open={showConsent}
        onAgreed={() => { setConsented(true); setShowConsent(false); }}
        onClose={() => setShowConsent(false)}
      />
    </div>
  );
}
